import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ChatBubbleOutline as ChatLinkIcon,
  Language as PortalLinkIcon,
  GroupAdd as AssignIcon,
  WhatsApp as WhatsAppIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import AtsAssignUsersDialog from './AtsAssignUsersDialog';
import AtsDeletePositionDialog from './AtsDeletePositionDialog';
import { canEditPosition } from './positionPermissions';
import AtsDeadlineChip from './AtsDeadlineChip';
import { getStatusColor, getStatusLabel, isVisibleInPortal } from './positionStatus';
import useOrgPublicSlug from './useOrgPublicSlug';
import { getPositionUrl } from '../../jobBoard/hostSlug';

const VISIBILITY_COLORS = {
  general: { label: 'General', color: 'default' },
  confidential: { label: 'Confidencial', color: 'warning' },
};

// El tipo de proceso puede venir vacío en vacantes antiguas, y 'vacancy' es el
// valor legado que hoy equivale a hunting (misma normalización que el wizard).
const normalizeTipo = (tipo) => (!tipo || tipo === 'vacancy' ? 'hunting' : tipo);

const AtsPositionsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project, authToken, showAlert, user } = useStore.getState();
  // Subdominio de la organización para el link público de cada vacante
  const publicSlug = useOrgPublicSlug();

  // Solo los jefes pueden asignar responsables a una vacante
  const isAdmin = ['admin', 'admin-grafton', 'super-admin'].includes(user?.rol);

  const [positions, setPositions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const loadPositions = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(
          `/positions`,
          {
            params: { projectId: project, source: 'ats' },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.response?.status_response === 'OK') {
          setPositions(response.data.response.data || []);
        } else {
          setError(t('ats.positions.loadError'));
        }
      } catch (err) {
        console.error(err);
        setError(t('ats.positions.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (project) loadPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFiltered(positions);
      return;
    }

    const term = searchTerm.toLowerCase();

    setFiltered(
      positions.filter(
        (p) =>
          p.name?.toLowerCase().includes(term) ||
          p.internalName?.toLowerCase().includes(term) ||
          p.industria?.toLowerCase().includes(term)
      )
    );
  }, [searchTerm, positions]);

  // Link del portal: hunting y masivas con "Visible en portal"
  // (PLAN_MASIVO_PORTAL). Desde 2026-10-25 lleva al detalle de la vacante en la
  // web de empleos ({slug}.amandahr.ai/vacante/:id), donde recién al apretar
  // Postular se pide el correo. Las masivas tienen además el link del chat de
  // captación (/postular/:id) y el de WhatsApp, cada uno con su ícono.
  const handleCopyPortalUrl = (e, position) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getPositionUrl(position.uid, publicSlug));
    showAlert(t('ats.positions.portalUrlCopied'), 'success');
  };

  const handleCopyChatUrl = (e, position) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/postular/${position.uid}`);
    showAlert(t('ats.captation.chatLink.copied'), 'success');
  };

  // El número de WhatsApp es el mismo para todas las filas de una organización:
  // se pide en el primer clic y queda cacheado, en vez de un request por vacante.
  const whatsappNumberRef = useRef(null);

  const handleCopyWhatsappLink = async (e, position) => {
    e.stopPropagation();
    try {
      // Número de la organización (2026-10-26): el propio, el compartido de
      // Grafton si un super-admin lo permitió, o ninguno. Cacheado por
      // organización; sin número se vuelve a preguntar (como antes), por si
      // un super-admin lo permitió mientras tanto.
      const organizationId = position.organizationId || useStore.getState().organization;
      if (!whatsappNumberRef.current?.number || whatsappNumberRef.current.organizationId !== organizationId) {
        const { data } = await api.get('/captation/whatsapp-info', {
          params: { organizationId },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        whatsappNumberRef.current = { organizationId, number: data.response?.data?.number || null };
      }

      const number = whatsappNumberRef.current.number;
      if (!number) {
        showAlert(t('ats.captation.whatsappLink.noNumber'), 'error');
        return;
      }

      // Mismo formato que el botón del encabezado de la vacante: el webhook
      // lee el [REF:positionId] y entra directo a esa vacante.
      const text = `¡Hola! Vengo por el cargo ${position.name || ''} [${position.uid}]`;
      const link = `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
      await navigator.clipboard.writeText(link);
      showAlert(t('ats.captation.whatsappLink.copied'), 'success');
    } catch (err) {
      console.error('Error copiando link de WhatsApp:', err);
      showAlert(t('ats.captation.whatsappLink.error'), 'error');
    }
  };

  // Vacante eliminada (borrado lógico): sale de la tabla sin recargar
  const handleDeleted = (positionUid) => {
    setPositions((prev) => prev.filter((p) => p.uid !== positionUid));
  };

  const handleAssignSaved = (positionUid, usuariosAsignados) => {
    setPositions((prev) =>
      prev.map((p) =>
        p.uid === positionUid ? { ...p, usuariosAsignados } : p
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('ats.positions.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {filtered.length} {t('ats.positions.positionsFound')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/ats/positions/new')}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
        >
          {t('ats.positions.newPosition')}
        </Button>
      </Box>

      <TextField
        size="small"
        placeholder={t('ats.positions.searchPositions')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3, width: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? t('ats.positions.noPositionsSearch') : t('ats.positions.noPositions')}
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.positions.positionName')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.positions.processType')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.positions.industry')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.positions.modality')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.positions.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.title')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.positions.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((position) => {
                const totalCandidates = position.totalApplications || 0;

                return (
                  <TableRow
                    key={position.uid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/ats/positions/${position.uid}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {position.internalName || position.name}
                      </Typography>
                      {position.codigoRef && (
                        <Typography variant="caption" color="text.secondary">
                          {position.codigoRef}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {t(`ats.positions.wizard.options.tipo.${normalizeTipo(position.tipo)}`)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{position.industria}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{position.modalidad || '—'}</Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={getStatusLabel(position.status, t)}
                          color={getStatusColor(position.status)}
                          size="small"
                        />
                        {position.visibility === 'confidential' && (
                          <Chip
                            label={VISIBILITY_COLORS.confidential.label}
                            color={VISIBILITY_COLORS.confidential.color}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <AtsDeadlineChip position={position} />
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{totalCandidates}</Typography>
                    </TableCell>

                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {position.tipo === 'massive' && (
                        <Tooltip title={t('ats.captation.whatsappLink.button')}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleCopyWhatsappLink(e, position)}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {position.tipo === 'massive' && (
                        <Tooltip title={t('ats.captation.chatLink.button')}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleCopyChatUrl(e, position)}
                          >
                            <ChatLinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Link del portal (hunting y masivas). Sin "Visible en
                          portal" su link muestra "no disponible": no se ofrece */}
                      <Tooltip
                        title={t(isVisibleInPortal(position) ? 'ats.positions.copyPortalUrl' : 'ats.positions.copyPortalUrlHidden')}
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => handleCopyPortalUrl(e, position)}
                            disabled={!isVisibleInPortal(position)}
                          >
                            <PortalLinkIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {isAdmin && (
                        <Tooltip title={t('ats.positions.assignUsers')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignTarget(position);
                            }}
                          >
                            <AssignIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('ats.positions.view')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/ats/positions/${position.uid}`)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEditPosition(position, user) && (
                        <Tooltip title={t('ats.positions.deleteDialog.button')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(position);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isAdmin && (
        <AtsAssignUsersDialog
          open={!!assignTarget}
          position={assignTarget}
          onClose={() => setAssignTarget(null)}
          onSaved={handleAssignSaved}
        />
      )}

      <AtsDeletePositionDialog
        open={!!deleteTarget}
        positionId={deleteTarget?.uid}
        positionName={deleteTarget?.internalName || deleteTarget?.name}
        candidatesCount={deleteTarget?.totalApplications || 0}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />
    </Box>
  );
};

export default AtsPositionsLayout;